import React, { useState, useContext, useEffect } from 'react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../utils/secure-route/AuthContext';
import { useToastHandler } from '../../common/ToastHandler';
import ReusableModal from '../AppPAges/ManageSettings/ReusableModal';
import ReusableTable from '../../common/ReusableComponent/reusableTable/ReusableTable';
import useSwal from '../../common/ReusableComponent/useSwal';
import { ToastContainer } from 'react-toastify';
import PaginatedSearchSelect  from '../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect'
import useMyPermissions from '../../../utils/permissions/useMyPermissions';
import Can from '../../../utils/permissions/Can';

function CameraData() {
  const { user } = useContext(AuthContext);
  const { can } = useMyPermissions();
  const { Post, Put, Delete, Get, GetPaginatedData } = useApi();
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [name, setName] = useState('');
  const [rtspUrl, setRtspUrl] = useState('');
  const [location, setLocation] = useState('');
  const [group, setGroup] = useState(null);

  useEffect(() => {
    Get('cameraGroupData').then(data => {
      const list = Array.isArray(data) ? data : data?.results || [];
      setGroups(list);
    }).catch(() => setGroups([]));
  }, []);

  const triggerRefresh = () => setRefreshTrigger(p => p + 1);

  const fetchData = async ({ page = 1, searchTerm = '' } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData('cameraData', { page, search: searchTerm });
      const data = response?.data || [];
      const totalPages = Math.ceil((response?.totalCount || data.length) / 10);
      return { data, totalPages };
    } catch (error) {
      console.error('Error fetching cameras:', error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName(''); setRtspUrl(''); setLocation(''); setGroup(null);
  };

  const handleShow = (idOrItem = null, maybeItem = null) => {
    const item = maybeItem || (typeof idOrItem === 'object' ? idOrItem : null);
    if (item) {
      setEditing(item);
      setName(item.name);
      setRtspUrl(item.rtsp_url);
      setLocation(item.location);
      const groupObj = groups.find(g => g.id === item.group);
      setGroup(groupObj || null);
    } else {
      setEditing(null);
      resetForm();
    }
    setShowModal(true);
  };
  const handleClose = () => { setShowModal(false); resetForm(); };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { store: user.store, name, rtsp_url: rtspUrl, location, group: group?.id || group };
    try {
      if (editing) {
        await Put('cameraData', editing.id, payload);
        handleToast({ type: 'SUCCESS', message: 'Camera updated successfully!' });
      } else {
        await Post('cameraData', payload);
        handleToast({ type: 'SUCCESS', message: 'Camera added successfully!' });
      }
      handleClose();
      triggerRefresh();
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Error saving camera.' });
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      await Delete('cameraData', id);
      handleToast({ type: 'SUCCESS', message: 'Camera deleted successfully!' });
      triggerRefresh();
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Error deleting camera.' });
    }
  };

  const columns = [
    { header: 'Name', field: 'name' },
    { header: 'Group', field: 'group_name' },
    { header: 'Location', field: 'location' },
    { header: 'RTSP URL', field: 'rtsp_url' },
    { header: 'Status', field: 'status' },
  ];

  return (
    <>
      <div className="responsive-box" style={{ padding: '20px' }}>
        <div className="header-row">
          <div className="setting-title">Cameras</div>
          <Can module="mointerService" action="add">
            <button
              onClick={() => handleShow()}
              style={{
                backgroundColor: '#FFAF32',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                height: '42px',
              }}
            >
              + Add Camera
            </button>
          </Can>
        </div>

        <ReusableModal
          show={showModal}
          handleClose={handleClose}
          title={editing ? 'Edit Camera' : 'Add Camera'}
          width="560px"
        >
          <form onSubmit={handleSubmit}>
            <div className="d-flex flex-column mt-4" style={{ gap: '16px' }}>
              <div className="d-flex" style={{ gap: '16px' }}>
                <div className="form-group product-field">
                  <input
                    type="text"
                    className="form-control-sales"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                  />
                  <label className="floating-label">Camera Name</label>
                </div>
                <div className="form-group product-field">
                  <input
                    type="text"
                    className="form-control-sales"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    required
                  />
                  <label className="floating-label">Location</label>
                </div>
              </div>

              <div className="d-flex" style={{ gap: '16px' }}>
                <div className="form-group product-field">
                  <input
                    type="text"
                    className="form-control-sales"
                    value={rtspUrl}
                    onChange={e => setRtspUrl(e.target.value)}
                    placeholder="rtsp://192.168.1.7:8554/stream"
                    required
                  />
                  <label className="floating-label">RTSP URL</label>
                  {/* <div style={{ fontSize: '13px', color: '#616060', marginTop: '4px', fontWeight:800 }}>0 = Built-in camera &nbsp;|&nbsp; 1 = USB webcam</div> */}
                </div>
                <div className="form-group product-field">
                  <PaginatedSearchSelect
                    api="cameraGroupData"
                    GetPaginatedData={GetPaginatedData}
                    method="GET"
                    value={group}
                    onSelect={(selected) => setGroup(selected)}
                    getOptionLabel={(opt) => opt?.name || ""}
                    placeholder="Group"
                    pageSize={10}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#282e26',
                    color: '#F4FFEE',
                    borderRadius: '40px',
                    width: '160px',
                    height: '38px',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {editing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </ReusableModal>

        <ReusableTable
          fetchData={fetchData}
          columns={columns}
          showAction={true}
          enablePagination={true}
          showFooter={true}
          onEdit={can("mointerService", "change") ? handleShow : undefined}
          onDelete={can("mointerService", "delete") ? handleDelete : undefined}
          loading={loading}
          refreshKey={refreshTrigger}
        />
      </div>
      <ToastContainer />
    </>
  );
}

export default CameraData;
